/** Shared utilities for {@link StudentRow}, {@link StudentCard} and {@link StudentsDataTable}. */

import type { LucideIcon } from 'lucide-react';
import {
  normalizeStudentAccountStatus,
  type Student,
  type StudentFilters,
} from '@/types/inperson/student.types';
import type { GetUsersParams } from '@/types/user.types';
import {
  STUDENT_STATUS_CONFIG,
  EXIT_REASON_OPTIONS,
  DISABILITY_TYPE_OPTIONS,
  SPECIAL_NEEDS_TYPE_OPTIONS,
  type StudentStatusConfig,
} from '@/components/students/student-configs';
import { getStudentGenderConfig } from '@/utils/common-utils';

export { getStudentStatusDetailColorName } from '@/utils/common-utils';

/** RBAC actions available on a student row/card. Single source of truth. */
export interface StudentPageActions {
  canEditRow?: boolean;
  canDeleteRow?: boolean;
}

/** Extended actions for {@link StudentsDataTable} (adds {@link DataTable}-level permissions). */
export interface StudentDataTablePageActions extends StudentPageActions {
  canExportCsv?: boolean;
}

/** Flattened status / profile / gender flags used by {@link StudentRow}. */
export interface StudentDisplay {
  statusKey: string;
  statusConfig: StudentStatusConfig;
  StatusIcon: LucideIcon;
  profile: NonNullable<Student['userProfiles']>[number] | undefined;
  levelLabel: string | null;
  isMinor: boolean | undefined;
  hasSpecialNeeds: boolean | undefined;
  genderConfig: ReturnType<typeof getStudentGenderConfig>;
}

/** Status badge, profile, level, minor/needs flags, and gender icon. */
export function deriveStudentDisplay(student: Student): StudentDisplay {
  const statusKey = normalizeStudentAccountStatus(String(student.status));
  const statusConfig = STUDENT_STATUS_CONFIG[statusKey] || STUDENT_STATUS_CONFIG.registered;
  const profile = student.userProfiles?.[0];
  const genderConfig = getStudentGenderConfig(profile?.gender ?? null);

  return {
    statusKey,
    statusConfig,
    StatusIcon: statusConfig.icon,
    profile,
    levelLabel: profile?.level?.label?.trim() || null,
    isMinor: profile?.isMinor,
    hasSpecialNeeds: profile?.hasSpecialNeeds,
    genderConfig,
  };
}

/** Comma-separated establishment names, or "-". */
export function formatStudentEstablishmentNames(student: Student): string {
  if (!student.userEstablishments || student.userEstablishments.length === 0) return '-';
  return (
    student.userEstablishments
      .filter((ue) => ue.establishment !== null)
      .map((ue) => ue.establishment.name)
      .join(', ') || '-'
  );
}

/** Entry date as fr-FR locale, or "-". */
export function formatStudentEntryDate(createdAt: Date | string | undefined): string {
  return createdAt ? new Date(createdAt).toLocaleDateString('fr-FR') : '-';
}

/** Exit-reason label from the catalog, or the raw value. */
export function getStudentExitReasonLabel(student: Student): string {
  const reason = student.deactivationReason;
  if (!reason) return '-';
  return (
    EXIT_REASON_OPTIONS.find((o) => o.value === reason)?.label ??
    (reason === 'Autre' ? 'Autre' : reason)
  );
}

/** Deactivation date, falling back to profile `exitDate`. */
export function getStudentDeactivationDate(student: Student): string | Date | null {
  const profile = student.userProfiles?.[0] as { exitDate?: string | Date | null } | undefined;
  return student.deactivationDate ?? profile?.exitDate ?? null;
}

/** Presence rate with a non-breaking space before `%`, or "-". */
export function formatStudentPresenceRate(student: Student): string {
  return student.presenceRate != null ? `${student.presenceRate}\u00a0%` : '-';
}

/** Display name, then first+last, then a fallback label. */
export function getStudentDisplayName(student: Student): string {
  if (student.displayName) return student.displayName;
  const full = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim();
  return full || 'Sans nom';
}

/** Subject count from the profile array. */
export function getStudentSubjectCount(profile: { subjects?: unknown } | undefined): number {
  const subjects = profile?.subjects;
  return Array.isArray(subjects) ? subjects.length : 0;
}

/** Subject-count label (`N` + singular/plural). */
export function formatStudentSubjectCountLabel(count: number): string {
  return `${count} matière${count > 1 ? 's' : ''}`;
}

/** Catalog label for a special-needs type, or the raw value. */
export function getSpecialNeedsTypeLabel(
  specialNeedsType: string | null | undefined,
): string | null {
  if (!specialNeedsType) return null;
  return (
    SPECIAL_NEEDS_TYPE_OPTIONS.find((o) => o.value === specialNeedsType.toLowerCase())?.label ??
    specialNeedsType
  );
}

/** Badge text; appends the free-text note when type is `autre`. */
export function getSpecialNeedsBadgeLabel(
  specialNeedsType: string | null | undefined,
  specialNeedsLabel: string | null,
  specialNeedsNote: string | null | undefined,
): string {
  if (!specialNeedsLabel) return 'Besoins spécifiques';
  if (specialNeedsType === 'autre' && specialNeedsNote) {
    return `Besoins spécifiques – ${specialNeedsLabel} : "${specialNeedsNote}"`;
  }
  return `Besoins spécifiques – ${specialNeedsLabel}`;
}

/** Disability chips; extra item when `autre` has a note. */
export function buildDisabilityTypeItems(
  disabilityTypes: string[] | null | undefined,
  disabilityOtherNote: string | null | undefined,
): Array<{ id: string; label: string }> {
  const items = (disabilityTypes ?? []).map((val) => {
    const opt = DISABILITY_TYPE_OPTIONS.find((o) => o.value === val);
    return { id: val, label: opt?.label ?? val };
  });
  if (disabilityTypes?.includes('autre') && disabilityOtherNote) {
    items.push({ id: 'autre-note', label: disabilityOtherNote });
  }
  return items;
}

/** Resolve subject ids against the catalog; unknown ids are dropped. */
export function resolveStudentSubjectLabels(
  subjectIds: string[],
  allSubjects: Array<{ id: string; label: string }>,
): Array<{ id: string; label: string }> {
  return subjectIds
    .map((id) => {
      const subject = allSubjects.find((s) => s.id === id);
      return subject ? { id: subject.id, label: subject.label } : null;
    })
    .filter((s): s is { id: string; label: string } => s !== null);
}

/** True when any personal-info field is present (hide empty section). */
export function hasStudentPersonalInfo(fields: {
  dateOfBirth?: unknown;
  genderLabel: string | null;
  addressLine1?: unknown;
  department?: unknown;
  postalCode?: unknown;
  city?: unknown;
}): boolean {
  return Boolean(
    fields.dateOfBirth ||
    fields.genderLabel != null ||
    fields.addressLine1 ||
    fields.department ||
    fields.postalCode ||
    fields.city,
  );
}

/** Map UI filters + pagination to in-person student list params. */
export function buildStudentInPersonListQueryParams(
  filters: StudentFilters,
  options: { page?: number; pageSize?: number } = {},
): GetUsersParams {
  return {
    page: options.page,
    pageSize: options.pageSize,
    search: filters.search?.trim() || undefined,
    statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
    subjectIds: filters.subjects.length > 0 ? filters.subjects : undefined,
    levelIds: filters.levelIds.length > 0 ? filters.levelIds : undefined,
    classIds: filters.classIds.length > 0 ? filters.classIds : undefined,
    specialtyIds:
      filters.specialtyIds && filters.specialtyIds.length > 0 ? filters.specialtyIds : undefined,
    establishmentId: filters.establishmentId,
    dateFrom: filters.createdFrom,
    dateTo: filters.createdTo,
    isMinor: filters.isMinor,
    hasSpecialNeeds: filters.hasSpecialNeeds,
    accompanimentTypes:
      filters.accompanimentTypes.length > 0 ? filters.accompanimentTypes : undefined,
    exitDateFrom: filters.exitDateFrom,
    exitDateTo: filters.exitDateTo,
    exitReason: filters.exitReason,
  };
}

/** True when any in-person student chip / date / search filter is set. */
export function hasStudentInPersonActiveFilters(filters: StudentFilters): boolean {
  return (
    !!filters.search?.trim() ||
    filters.statuses.length > 0 ||
    filters.levelIds.length > 0 ||
    filters.subjects.length > 0 ||
    filters.classIds.length > 0 ||
    (filters.specialtyIds && filters.specialtyIds.length > 0) ||
    filters.establishmentId !== undefined ||
    filters.createdFrom !== undefined ||
    filters.createdTo !== undefined ||
    filters.isMinor !== undefined ||
    filters.hasSpecialNeeds !== undefined ||
    filters.accompanimentTypes.length > 0 ||
    filters.exitDateFrom !== undefined ||
    filters.exitDateTo !== undefined ||
    filters.exitReason !== undefined
  );
}
